/* eslint-disable no-fallthrough */
/* eslint-disable max-len */
import React, {
  useState, useEffect, useContext, useMemo, useCallback, useRef,
} from 'react';
import {
  Box, Button, FormControlLabel, Radio, RadioGroup, TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import NavigationLayout from '../../components/NavigationLayout';
import CustomTable from '../../components/table/CustomTable';
import DropDown from '../../components/dropdown';
import theme from './theme';
import TabsWithPanels from '../../components/tabsWithPanel';
import { UserContext } from '../../contexts/User';
import useDebounce from '../../hooks/useDebounce';
import ViewRequestModal from './ViewRequestModal';
import ApproveRequestModal from './ApproveRequestModal';
import DenyRequestModal from './DenyRequestModal';
import RequestModal from './RequestModal';

const FIXED_TABLE_HEADERS = [
  {
    label: 'Member',
    sortable: true,
  },
  {
    label: 'Policy Name',
    sortable: true,
  },
  {
    label: 'Reason',
  },
  {
    label: 'Start',
  },
  {
    label: 'End',
  },
  {
    label: 'Hours Requested',
    sortable: true,
  },
  {
    label: 'Status',
    sortable: true,
  },
  {
    label: 'Requested On',
  },
  {
    label: 'Action',
  },
];

const APPROVED_TABLE_HEADERS = [
  {
    label: 'Member',
    sortable: true,
  },
  {
    label: 'Policy Name',
    sortable: true,
  },
  {
    label: 'Reason',
  },
  {
    label: 'Start',
  },
  {
    label: 'End',
  },
  {
    label: 'Hours Requested',
    sortable: true,
  },
  {
    label: 'Status',
    sortable: true,
  },
  {
    label: 'Requested On',
  },
  {
    label: 'Approved By',
  },
  {
    label: 'Action',
  },
];

const DENIED_TABLE_HEADERS = [
  {
    label: 'Member',
    sortable: true,
  },
  {
    label: 'Policy Name',
    sortable: true,
  },
  {
    label: 'Reason',
  },
  {
    label: 'Start',
  },
  {
    label: 'End',
  },
  {
    label: 'Hours Requested',
    sortable: true,
  },
  {
    label: 'Status',
    sortable: true,
  },
  {
    label: 'Requested On',
  },
  {
    label: 'Denied By',
  },
  {
    label: 'Action',
  },
];

const TAB_KEYS = ['Submitted', 'Approved', 'Denied', 'All'];
const PAGE_SIZE = 10;
const TABLE_STYLES = {
  container: {
    marginTop: 1,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '14px',
    fontWeight: 600,
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    fontSize: '14px',
    color: 'var(--dark-2)',
    whiteSpace: 'nowrap',
  },
};

const buildInitialPages = () => TAB_KEYS.reduce((acc, key) => ({ ...acc, [key]: 1 }), {});
const buildInitialPagination = () => TAB_KEYS.reduce((acc, key) => ({
  ...acc,
  [key]: { totalPages: 0, totalCounts: 0 },
}), {});

function PanelAction({
  handleClick,
  onRequestButtonHover,
  disabled,
  scope,
  onScopeChange,
  canViewAll,
  searchTerm,
  onSearchChange,
}) {
  return (
    <Box
      alignSelf="center"
      marginLeft="auto"
      display="flex"
      gap={2}
      flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
      sx={{ alignItems: 'center', minWidth: 420 }}
    >
      <TextField
        value={searchTerm}
        onChange={(event) => onSearchChange(event.target.value)}
        size="small"
        placeholder="Search team members"
        sx={{
          minWidth: 220,
          flex: 1,
          maxWidth: 320,
        }}
      />
      <RadioGroup
        row
        value={scope}
        onChange={(event) => onScopeChange(event.target.value)}
        sx={{ flexShrink: 0 }}
      >
        <FormControlLabel value="direct" control={<Radio size="small" />} label="Direct reports" />
        <FormControlLabel value="all" control={<Radio size="small" />} label="All members" disabled={!canViewAll} />
      </RadioGroup>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleClick}
        onMouseEnter={onRequestButtonHover}
        onFocus={onRequestButtonHover}
        disabled={disabled}
        sx={{
          backgroundColor: '#53A551',
          color: 'white',
          textTransform: 'unset',
          whiteSpace: 'nowrap',
        }}
      >
        Request PTO
      </Button>
    </Box>
  );
}

function MembersPto() {
  const { member } = useContext(UserContext);
  const [requests, setRequests] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [hasLoadedTeamMembers, setHasLoadedTeamMembers] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [pageByStatus, setPageByStatus] = useState(buildInitialPages);
  const [paginationByStatus, setPaginationByStatus] = useState(buildInitialPagination);
  const [statusCounts, setStatusCounts] = useState({
    Submitted: 0, Approved: 0, Denied: 0, All: 0,
  });
  const [scope, setScope] = useState('direct');
  const [canViewAll, setCanViewAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const teamMembersLoadRef = useRef(null);
  const { debounce } = useDebounce();

  const currentStatus = TAB_KEYS[activeTab];

  const getTeamMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await sendRequest({
        method: 'get', url: API.WORKSPACE.members(member.workspaceId),
      });
      if (response.data) {
        // Filter to get direct reports (subordinates)
        const directReports = response.data.members.filter(
          (m) => m.reportingManager === member.id,
        );
        // Include self (manager) and subordinates
        const selfAndSubordinates = [
          // response.data.members.find((m) => m.id === member.id),
          ...directReports,
        ].filter(Boolean); // Remove any undefined values
        setTeamMembers(selfAndSubordinates);
      }
      setHasLoadedTeamMembers(true);
    } finally {
      setLoadingMembers(false);
    }
  };

  const ensureTeamMembersLoaded = useCallback(async () => {
    if (hasLoadedTeamMembers) {
      return;
    }

    if (!teamMembersLoadRef.current) {
      teamMembersLoadRef.current = getTeamMembers().finally(() => {
        teamMembersLoadRef.current = null;
      });
    }

    await teamMembersLoadRef.current;
  }, [hasLoadedTeamMembers]);

  const handlePrefetchTeamMembers = useCallback(() => {
    if (!hasLoadedTeamMembers && !loadingMembers) {
      ensureTeamMembersLoaded().catch(() => {
        // Keep hover/focus prefetch best-effort and non-blocking.
      });
    }
  }, [ensureTeamMembersLoaded, hasLoadedTeamMembers, loadingMembers]);

  const handleOpenRequestModal = useCallback(async () => {
    await ensureTeamMembersLoaded().catch(() => {
      // Keep previous UX: allow opening modal even if member fetch fails.
    });
    setShowRequestModal(true);
  }, [ensureTeamMembersLoaded]);

  const fetchRequests = useCallback(async (
    statusKey,
    page,
    requestedScope = scope,
    requestedSearch = searchTerm,
  ) => {
    const safePage = Math.max(page || 1, 1);
    setLoadingRequests(true);
    try {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.getTeamOffTimeData(member.workspaceId, member.id),
        params: {
          page: safePage,
          pageSize: PAGE_SIZE,
          includeStatusCounts: false,
          scope: requestedScope,
          search: requestedSearch || undefined,
          ...(statusKey !== 'All' ? { status: statusKey } : {}),
        },
      });

      if (response.success && response.data) {
        setRequests(response.data.offTimeRequests || []);
        setCanViewAll(!!response.data.canViewAll);

        // eslint-disable-next-line no-nested-ternary
        const filteredCounts = typeof response.data.filteredCounts === 'number'
          ? response.data.filteredCounts
          : 0;

        setStatusCounts((prev) => ({
          ...prev,
          [statusKey]: filteredCounts,
        }));

        // eslint-disable-next-line no-nested-ternary
        const totalPages = typeof response.data.totalPages === 'number'
          ? response.data.totalPages
          : (filteredCounts ? Math.ceil(filteredCounts / PAGE_SIZE) : 0);

        const resolvedPage = response.data.currentPage
          || Math.min(safePage, Math.max(totalPages, 1));

        setPaginationByStatus((prev) => ({
          ...prev,
          [statusKey]: {
            totalPages,
            totalCounts: filteredCounts,
          },
        }));
        setPageByStatus((prev) => ({
          ...prev,
          [statusKey]: resolvedPage,
        }));
      } else {
        setRequests([]);
        setStatusCounts({
          Submitted: 0, Approved: 0, Denied: 0, All: 0,
        });
        setCanViewAll(false);
        setPaginationByStatus(buildInitialPagination());
        setPageByStatus(buildInitialPages());
      }
    } finally {
      setLoadingRequests(false);
    }
  }, [member.id, member.workspaceId, scope, searchTerm]);

  const refreshCurrentTab = useCallback(() => {
    fetchRequests(currentStatus, pageByStatus[currentStatus] || 1, scope, searchTerm);
  }, [currentStatus, fetchRequests, pageByStatus, scope, searchTerm]);

  useEffect(() => {
    fetchRequests(TAB_KEYS[0], 1, scope, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setHasLoadedTeamMembers(false);
    setTeamMembers([]);
    teamMembersLoadRef.current = null;
  }, [member.id, member.workspaceId]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const tabKey = TAB_KEYS[newTab];
    const nextPage = pageByStatus[tabKey] || 1;
    setRequests([]);
    fetchRequests(tabKey, nextPage, scope, searchTerm);
  };

  const handleScopeChange = (value) => {
    const nextScope = value;
    setScope(nextScope);
    setPageByStatus(buildInitialPages());
    setPaginationByStatus(buildInitialPagination());
    setStatusCounts({
      Submitted: 0, Approved: 0, Denied: 0, All: 0,
    });
    setRequests([]);
    fetchRequests(currentStatus, 1, nextScope, searchTerm);
  };

  const handlePageChange = useCallback((tabKey, page) => {
    setPageByStatus((prev) => ({
      ...prev,
      [tabKey]: page,
    }));
    fetchRequests(tabKey, page, scope, searchTerm);
  }, [fetchRequests, scope, searchTerm]);

  const handleSearchChange = useMemo(() => debounce((value) => {
    setPageByStatus(buildInitialPages());
    setPaginationByStatus(buildInitialPagination());
    fetchRequests(currentStatus, 1, scope, value);
  }, 300), [debounce, fetchRequests, currentStatus, scope]);

  const tableRows = useMemo(() => {
    if (!requests?.length) return [];

    return requests.map((request) => {
      const dateOptions = {
        weekday: 'short',
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      };
      const startDate = new Date(request.startDate);
      const endDate = new Date(request.endDate);
      const createDate = new Date(request.createdAt);
      const hours = Math.floor(request.totalHours / 60) < 10 ? `0${Math.floor(request.totalHours / 60)}` : Math.floor(request.totalHours / 60);
      const minutes = request.totalHours % 60 < 10 ? `0${request.totalHours % 60}` : request.totalHours % 60;
      const options = [
        {
          text: 'View Request',
          fontColor: '#061232',
          handleClick: () => {
            setSelectedRequest(request);
            setShowViewModal(true);
          },
        },
        {
          text: 'Approve',
          fontColor: '#061232',
          handleClick: () => {
            setSelectedRequest(request);
            setShowApproveModal(true);
          },
        },
        {
          text: 'Deny Request',
          fontColor: '#CC4525',
          handleClick: () => {
            setSelectedRequest(request);
            setShowDenyModal(true);
          },
        },
      ];

      if (request.status === 'Approved') {
        options.splice(1, 1);
      } else if (request.status === 'Denied') {
        options.splice(2, 1);
      }

      const row = {
        member: `${request.member.user.firstName} ${request.member.user.lastName}`,
        policyName: request.offTimePolicy.name,
        reason: request.reason,
        start: startDate.toLocaleDateString('en-US', dateOptions),
        end: endDate.toLocaleDateString('en-US', dateOptions),
        hours: `${hours}:${minutes}`,
        status: request.status,
        requestOn: createDate.toLocaleDateString('en-US', dateOptions),
        action: (<DropDown title="Actions" options={options} />),
      };

      if (request.status === 'Approved') {
        row.approvedBy = request.manager?.user.fullName;
      } else if (request.status === 'Denied') {
        row.deniedBy = request.manager?.user.fullName;
      }

      return row;
    });
  }, [requests]);

  const tabItems = useMemo(() => [
    { label: 'Submitted', length: statusCounts.Submitted || 0 },
    { label: 'Approved', length: statusCounts.Approved || 0 },
    { label: 'Denied', length: statusCounts.Denied || 0 },
    { label: 'All', length: statusCounts.All || 0 },
  ], [statusCounts]);

  const tabPanels = useMemo(() => TAB_KEYS.map((tabKey) => {
    const headers = (() => {
      switch (tabKey) {
        case 'Approved':
          return APPROVED_TABLE_HEADERS;
        case 'Denied':
          return DENIED_TABLE_HEADERS;
        default:
          return FIXED_TABLE_HEADERS;
      }
    })();

    return (
      <CustomTable
        key={`tab-panel-${tabKey}`}
        headers={headers}
        items={tabKey === currentStatus ? tableRows : []}
        loading={loadingRequests}
        pages={paginationByStatus[tabKey]?.totalPages || 0}
        currentPage={pageByStatus[tabKey] || 1}
        handlePageChange={(page) => handlePageChange(tabKey, page)}
        handleSort={() => {}}
        styles={TABLE_STYLES}
      />
    );
  }), [currentStatus, handlePageChange, loadingRequests, pageByStatus, paginationByStatus, tableRows]);

  return (
    <ThemeProvider theme={theme}>
      <NavigationLayout title="Team Member's Time Off">
        <TabsWithPanels
          tabItems={tabItems}
          tabPanels={tabPanels}
          PanelAction={(
            <PanelAction
              handleClick={handleOpenRequestModal}
              onRequestButtonHover={handlePrefetchTeamMembers}
              disabled={loadingMembers}
              scope={scope}
              onScopeChange={handleScopeChange}
              canViewAll={canViewAll}
              searchTerm={searchTerm}
              onSearchChange={(value) => {
                setSearchTerm(value);
                handleSearchChange(value.trim());
              }}
            />
          )}
          value={activeTab}
          onChange={handleTabChange}
        />
      </NavigationLayout>
      {
        showViewModal && (
        <ViewRequestModal
          closeModal={() => setShowViewModal(false)}
          request={selectedRequest}
          handleApprove={() => setShowApproveModal(true)}
          handleDeny={() => setShowDenyModal(true)}
        />
        )
      }
      {
        showApproveModal && (
        <ApproveRequestModal
          closeModal={() => setShowApproveModal(false)}
          request={selectedRequest}
          getData={refreshCurrentTab}
        />
        )
      }
      {
        showDenyModal && (
        <DenyRequestModal
          closeModal={() => setShowDenyModal(false)}
          request={selectedRequest}
          getData={refreshCurrentTab}
        />
        )
      }
      {
        showRequestModal && (
        <RequestModal
          closeModal={() => {
            setShowRequestModal(false);
            refreshCurrentTab();
          }}
          teamMembers={teamMembers}
          isManagerInitiated
        />
        )
      }
    </ThemeProvider>
  );
}

PanelAction.propTypes = {
  handleClick: PropTypes.func.isRequired,
  onRequestButtonHover: PropTypes.func,
  disabled: PropTypes.bool,
  scope: PropTypes.string,
  onScopeChange: PropTypes.func,
  canViewAll: PropTypes.bool,
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func,
};

MembersPto.propTypes = {

};

export default MembersPto;
